import * as Sentry from "@sentry/react-router"
import {useEffect} from "react"
import {useTranslation} from "react-i18next"
import {
  Links,
  Meta,
  Outlet,
  Scripts,
  ScrollRestoration,
  data,
  isRouteErrorResponse,
  useRouteLoaderData,
} from "react-router"

import type {Route} from "./+types/root"
import stylesheet from "./app.css?url"
import {ErrorPage} from "./components/ErrorPage"
import {ToastNotifications} from "./components/ToastNotification"
import {ClientEnvContext} from "./lib/ClientEnvContext"
import {
  applicationModel,
  productionUserSessions,
} from "./lib/productionApplication.server"
import {createApplicationModelMiddleware} from "./middleware/applicationModel.server"
import {getLocale, i18nextMiddleware, localeCookie} from "./middleware/i18next"
import {utmParamsMiddleware} from "./middleware/utmParams"
import {AyaClientEnv, AyaEnv} from "~/lib/env.server"
import {
  createUserSessionsMiddleware,
  currentUserMiddleware,
  getCurrentUser,
} from "~/session.server"

export const middleware = [
  createApplicationModelMiddleware(applicationModel),
  createUserSessionsMiddleware(productionUserSessions),
  currentUserMiddleware,
  i18nextMiddleware,
  utmParamsMiddleware,
]

export const links: Route.LinksFunction = () => [
  {rel: "preconnect", href: "https://fonts.googleapis.com"},
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Rubik:ital,wght@0,300..900;1,300..900&display=swap",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Fjalla+One:wght@400&display=swap",
  },
  {
    rel: "stylesheet",
    href: stylesheet,
  },
  {rel: "icon", type: "image/svg+xml", href: "/favicon.svg"},
  {rel: "icon", type: "image/x-icon", href: "/favicon.ico"},
  {rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png"},
  {rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png"},
  {rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png"},
  {
    rel: "icon",
    type: "image/png",
    sizes: "192x192",
    href: "/android-chrome-192x192.png",
  },
  {
    rel: "icon",
    type: "image/png",
    sizes: "512x512",
    href: "/android-chrome-512x512.png",
  },
]

export const action = async () => {
  // Handle POSTs to this route to avoid errors that make their way to
  // Sentry when bots make random POST requests to /.
}

export const loader = async ({context}: Route.LoaderArgs) => {
  const user = await getCurrentUser({context})
  const locale = user?.language ?? getLocale(context)
  const googleAnalyticsId = AyaEnv.googleAnalyticsId

  return data(
    {locale, googleAnalyticsId, clientEnv: {...AyaClientEnv}},
    {headers: {"Set-Cookie": await localeCookie.serialize(locale)}},
  )
}

export const Layout = ({children}: {children: React.ReactNode}) => {
  const {i18n} = useTranslation()

  const {googleAnalyticsId, clientEnv} = (useRouteLoaderData("root") ??
    {}) as Route.ComponentProps["loaderData"]

  return (
    <html lang={i18n.language} dir={i18n.dir(i18n.language)}>
      <head>
        <meta charSet="utf-8" />
        <meta
          name="viewport"
          // viewport-fit=cover extends the layout viewport into the
          // screen's safe areas (home bar, notch). Without it, iOS
          // reserves those zones outside the page entirely — an
          // unstylable band where the browser composites its
          // translucent chrome over whatever content happens to be
          // there. With it, the page reaches the physical screen edge
          // and env(safe-area-inset-*) becomes available for padding.
          content="width=device-width, initial-scale=1, interactive-widget=resizes-content, viewport-fit=cover"
        />
        <Meta />
        <Links />
        {googleAnalyticsId && (
          <>
            <script
              async
              src={`https://www.googletagmanager.com/gtag/js?id=${googleAnalyticsId}`}
            />
            <script
              dangerouslySetInnerHTML={{
                __html: ` window.dataLayer = window.dataLayer || [];
    function gtag(){dataLayer.push(arguments);}
    gtag('js', new Date());
    gtag('config', '${googleAnalyticsId}');`,
              }}
            />
          </>
        )}
      </head>
      <body>
        <ClientEnvContext.Provider value={clientEnv}>
          {children}
        </ClientEnvContext.Provider>
        <ToastNotifications />
        <ScrollRestoration />
        <Scripts />
      </body>
    </html>
  )
}

export default function App({loaderData}: Route.ComponentProps) {
  const {locale} = loaderData
  const {i18n} = useTranslation()

  useEffect(() => {
    if (i18n.language !== locale) void i18n.changeLanguage(locale)
  }, [locale, i18n])

  return <Outlet />
}

export const ErrorBoundary = ({error}: Route.ErrorBoundaryProps) => {
  // Trying to access translation strings here causes a crash
  let heading = "Oops!"
  let details = "Something went wrong. Please try again later."
  let stackTrace: string | undefined

  if (isRouteErrorResponse(error) && error.status === 404) {
    heading = "404"
    details = "The requested page could not be found."
  } else if (error && error instanceof Error) {
    Sentry.captureException(error)
    if (import.meta.env.DEV) {
      details = error.message
      stackTrace = error.stack
    }
  }

  return (
    <ErrorPage
      heading={heading}
      details={details}
      homeButtonTitle="Go Home"
      stackTrace={stackTrace}
    />
  )
}
